import React from 'react';
import cx from 'classnames';
import { withStyles, WithStyles, createStyles, Theme } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';

import Badge from 'ha/ui/next/Badge';
import { withIntl, IntlProps } from 'ha/i18n';
import formatPrice from 'ha/utils/formatPrice';
import units from 'ha/theme/units';
import { NormalizedListing } from 'ha/modules/Algolia/Types';

import { MARKER_ZINDEX, MARKER_HOVERED_ZINDEX } from './constants';

const findCheapestRoom = (rooms: NormalizedListing[]) =>
  rooms.reduce(
    (expected, room) => (room.price < expected.price ? room : expected),
    rooms[0],
  );

interface Props {
  marker: {
    rooms: NormalizedListing[];
  };
  emphasis: boolean;
  highlightedRoomId: number;
}

const styles = (t: Theme) =>
  createStyles({
    wrapper: {
      height: '25px',
      position: 'absolute',
      left: 0,
      top: 0,
      marginTop: '-3px',
      whiteSpace: 'nowrap',
      display: 'flex',
      transform: 'translateX(-50%) translateY(-100%)',
      zIndex: MARKER_ZINDEX,
    },
    wrapperEmphasised: {
      zIndex: MARKER_HOVERED_ZINDEX,
    },
    marker: {
      transformOrigin: 'center bottom',
      transition:
        '-webkit-transform 0.25s cubic-bezier(0.485, 1.65, 0.545, 0.835)',
      cursor: 'pointer',
      '&:before': {
        position: 'absolute',
        content: '""',
        border: '3px solid',
        borderColor: `${t.palette.mint.main} transparent transparent transparent`,
        left: '50%',
        bottom: -6,
        marginLeft: '-3px',
      },
    },
    markerEmphasised: {
      transform: 'scale(2)',
    },
    content: {
      backgroundColor: t.palette.mint.main,
      color: 'white',
      borderRadius: units.rem(0.1),
      fontSize: units.rem(0.75),
      padding: units.yx(0.15, 0.3),
      overflow: 'visible',
      display: 'flex',
      alignItems: 'center',
    },
  });

export const Pin: React.FC<Props & IntlProps & WithStyles<typeof styles>> = ({
  classes,
  marker,
  emphasis,
  highlightedRoomId,
  intl: { T },
}) => {
  const highlightedRoom = marker.rooms.find(
    room => room.id === highlightedRoomId,
  );
  const cheapestRoom = findCheapestRoom(marker.rooms);
  const { price, currency } = highlightedRoom || cheapestRoom;
  const formattedPrice = formatPrice(price, currency);

  return (
    <div className={cx(classes.wrapper, emphasis && classes.wrapperEmphasised)}>
      <div
        className={cx(classes.marker, emphasis && classes.markerEmphasised)}
        role="button"
        data-test-locator="Search Map Pin"
      >
        {marker.rooms.length > 1 ? (
          <Badge badgeContent={marker.rooms.length} color="secondary">
            <Typography variant="button" className={classes.content}>
              {T('From')} {formattedPrice}
            </Typography>
          </Badge>
        ) : (
          <Typography variant="button" className={classes.content}>
            {formattedPrice}
          </Typography>
        )}
      </div>
    </div>
  );
};

export default withStyles(styles)(withIntl(Pin));
